"use client"

import {
  PersonListCard,
  PersonListCardSummary,
  personDescriptionFromProfile,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Label,
  MatchQualityBadge,
  ListCardTrailingActionsFooter,
  EvaluationAnalysisInline,
  candidateMatchToPersonModel,
  openToWorkTitleBadge,
} from '@yeshuman/sdk'
import type { OpenPersonMatchHandler } from '@yeshuman/sdk'
import { type ReactNode, useState } from 'react'
import type { ApplicantProfileSummary } from '../../hooks/use-opportunity-applicants'
import { CandidateMatch } from '../../hooks/use-briefs'
import { useBriefs, useSaveCandidate } from '../../hooks/use-briefs'
import { Save } from 'lucide-react'
import { toast } from 'sonner'
import type { OpenJobWorkspacePersonHandler } from '../../lib/job-workspace-person-paths'

interface CandidateCardProps {
  candidate: CandidateMatch
  briefId?: string
  autoSaveBriefId?: string
  hideSaveButton?: boolean
  /** When `hideSaveButton`, non-recruiter actions (e.g. Invite) rendered in the card footer. */
  additionalFooter?: ReactNode
  onOpenPerson?: OpenJobWorkspacePersonHandler | OpenPersonMatchHandler
  /** Job-scoped profile fields joined from the applicant board (headline, skills). */
  profileSummary?: ApplicantProfileSummary | null
}

export function CandidateCard({
  candidate,
  briefId,
  autoSaveBriefId,
  hideSaveButton,
  additionalFooter,
  onOpenPerson,
  profileSummary,
}: CandidateCardProps) {
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [selectedBriefId, setSelectedBriefId] = useState(briefId || '')
  const [notes, setNotes] = useState('')

  const { briefs } = useBriefs()
  const { saveCandidate, isSaving } = useSaveCandidate()

  const handleSave = () => {
    const targetBriefId = autoSaveBriefId || selectedBriefId
    if (!targetBriefId) {
      toast.error('Please select a brief')
      return
    }

    saveCandidate(
      {
        briefId: targetBriefId,
        data: {
          profile_id: candidate.profile_id,
          notes: notes.trim() || undefined,
        },
      },
      {
        onSuccess: (result) => {
          toast.success(`Saved to brief: ${result.brief_title}`)
          setSaveDialogOpen(false)
          setNotes('')
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to save candidate')
        },
      },
    )
  }

  const handleDirectSave = () => {
    if (!autoSaveBriefId) return
    handleSave()
  }

  const saveButton = autoSaveBriefId ? (
    <Button size="sm" variant="outline" onClick={handleDirectSave} disabled={isSaving}>
      <Save className="mr-1.5 shrink-0" aria-hidden />
      {isSaving ? 'Saving...' : 'Save to Brief'}
    </Button>
  ) : (
    <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" type="button">
          <Save className="mr-1.5 shrink-0" aria-hidden />
          Save to Brief
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save candidate to brief</DialogTitle>
          <DialogDescription>Select a brief to save this candidate to</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brief-select">Brief</Label>
            <Select value={selectedBriefId} onValueChange={setSelectedBriefId}>
              <SelectTrigger id="brief-select">
                <SelectValue placeholder="Select a brief" />
              </SelectTrigger>
              <SelectContent>
                {briefs.map((brief) => (
                  <SelectItem key={brief.id} value={brief.id}>
                    {brief.title} ({brief.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this candidate..."
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving || !selectedBriefId}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )

  const footerInner = hideSaveButton
    ? additionalFooter
    : additionalFooter ? (
        <>
          {saveButton}
          {additionalFooter}
        </>
      ) : (
        saveButton
      )

  const footerContent = footerInner != null ? (
    <ListCardTrailingActionsFooter>{footerInner}</ListCardTrailingActionsFooter>
  ) : undefined

  return (
    <PersonListCard
      variant="evaluation"
      person={candidateMatchToPersonModel(candidate, profileSummary)}
      titleBadge={openToWorkTitleBadge(profileSummary?.open_to_work)}
      onOpen={
        onOpenPerson
          ? () =>
              onOpenPerson(
                candidate.profile_id,
                candidate.candidate_name,
                candidate.evaluation_id,
              )
          : undefined
      }
      openAriaLabel={
        onOpenPerson ? `Open match analysis for ${candidate.candidate_name}` : undefined
      }
      headerAside={
        candidate.rank != null ? (
          <Badge variant="default" className="shrink-0 self-start">
            #{candidate.rank}
          </Badge>
        ) : null
      }
      meta={
        <div className="flex flex-wrap gap-2">
          {typeof candidate.final_score === 'number' ? (
            <MatchQualityBadge score={candidate.final_score} />
          ) : null}
          {candidate.was_llm_judged ? (
            <Badge variant="outline" className="text-xs font-normal">
              LLM Evaluated
            </Badge>
          ) : null}
          {candidate.found_by?.map((strategy) => (
            <Badge key={strategy} variant="secondarySoft" className="text-xs font-normal">
              {strategy}
            </Badge>
          ))}
        </div>
      }
      body={
        <div className="space-y-3">
          <PersonListCardSummary
            description={personDescriptionFromProfile(profileSummary)}
            skills={profileSummary?.top_skills}
          />
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span>
              <span className="text-muted-foreground">Structured: </span>
              <span className="font-medium text-foreground">
                {(candidate.structured_score * 100).toFixed(0)}%
              </span>
            </span>
            <span>
              <span className="text-muted-foreground">Semantic: </span>
              <span className="font-medium text-foreground">
                {(candidate.semantic_score * 100).toFixed(0)}%
              </span>
            </span>
            {candidate.llm_score ? (
              <span>
                <span className="text-muted-foreground">LLM: </span>
                <span className="font-medium text-foreground">
                  {(candidate.llm_score * 100).toFixed(0)}%
                </span>
              </span>
            ) : null}
          </div>
          {candidate.evaluation_id ? (
            <EvaluationAnalysisInline
              evaluationId={candidate.evaluation_id}
              analysisHeadline={candidate.analysis_headline}
            />
          ) : candidate.llm_reasoning ? (
            <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {candidate.llm_reasoning}
              </p>
            </div>
          ) : null}
        </div>
      }
      footer={footerContent ?? undefined}
      footerClassName={footerContent ? 'pointer-events-auto px-0' : undefined}
    />
  )
}
